<template lang="pug">
  ProgramSettingsFormWrapper(
    :form-data="formData"
    :disable-save.sync="disableSave"
    :keys-to-reset="keysToReset"
    title="Teams Program Settings"
    formKey="TeamsSettings"
  )
    template
      WtTile(ancestor)
        WtTile(parent :col="6")
          WtTile(child :col="12").has-background-light
            .is-flex.mb-4
              WtTitle(card-title).mr-5.mt-1 Teams
              WtRadioQuestionInput
                template(v-slot:radioOne)
                  BRadio(
                    v-model="teamsOff"
                    :native-value="0"
                    @input="changeTeamsOff(0)"
                  ) On
                template(v-slot:radioTwo)
                  BRadio(
                    v-model="teamsOff"
                    :native-value="1"
                    @input="confirmTeamRemoval"
                  ) Off
            span.is-size-5 Turning the Teams Feature off will remove the Teams page for members and disable team challenges
        WtTile(parent :col="6" :class="{'disabled': teamsOff}")
          WtTile(child :col="12").has-background-light.is-flex.is-align-items-center
            .columns.full-width
              .column.is-half.is-flex.is-flex-direction-column.is-align-items-center
                BField(
                  label="Max Team Size"
                  label-for="max-team-size"
                )
                BNumberinput#max-team-size(
                  type="is-primary"
                  controls-position="compact"
                  controls-rounded
                  name="max-team-size"
                  v-model="maxTeamSize"
                  placeholder="5"
                  @input="handleMaxTeamSizeInput"
                  min="1"
                  max="1000"
                )
                span(v-if="maxTeamSize > 1000").has-text-danger.mt-2.has-text-centered The maximum team size is 1000 members.
              .column.is-half.is-flex.is-flex-direction-column.is-align-items-center
                WtButton.mb-3(
                  semibold
                  size="medium"
                  color="primary"
                  isRouterLink
                  :to="{ name: 'MemberTeamBuilder', params: { mode: 'admin' } }"
                  :disabled="maxTeamSize === 0 || !maxTeamSizeSaved"
                ) Create a Team
                span(v-if="!maxTeamSizeSaved").has-text-danger Changes must be saved before you can create a new team.
                WtRouterLink.is-size-5(
                  isRouterLink
                  to="TeamCenterSettingsCreateMultipleTeams"
                ) Create Multiple Teams

      WtTile(ancestor)
        WtTile(parent :class="{'disabled': teamsOff}")
          WtTile(child).has-background-light
            WtTitle(card-title) Team Management
            WtRadioQuestionInput(isFancyRadio)
              template(v-slot:radioOne)
                BRadio(
                  v-model="memberCanManageTeams"
                  :native-value="1"
                  @input="disableSave = false"
                ) Members can create, join, and leave teams at-will
              template(v-slot:radioOneText)
                span Members can create and manage their own teams, and can leave and join teams at-will.
              template(v-slot:radioTwo)
                BRadio(
                  v-model="memberCanManageTeams"
                  :native-value="0"
                  @input="disableSave = false"
                ) Only admins can create and manage teams.
              template(v-slot:radioTwoText)
                span Admins create and manage teams. Members can not create, leave, or join a different team.
      WtTile(ancestor)
        WtTile(parent)
          WtTile(child).has-background-light
            .columns
              .column(:class="{'disabled': teamsOff}")
                WtSwitchQuestionInput(
                  label="Allow members to join a team at registration?"
                  v-model="teamJoinerOn"
                  @input="disableSave = false"
                )
                WtSwitchQuestionInput(
                  v-if="teamJoinerOn"
                  label="Make joining a team optional at registration?"
                  v-model="teamJoiningOptional"
                  @input="disableSave = false"
                )
              .column(v-if="$auth.user().isWtAdmin")
                WtSwitchQuestionInput(
                  label="Team Invite Notification Emails"
                  :tooltip-text="teamInviteNotificationsTooltip"
                  v-model="teamInviteEmails"
                  @input="disableSave = false"
                  name="team invite emails"
                )
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EventBus from "@/eventBus";
import { numberUtilityMixin } from "@/mixins/numberUtilityMixin";
import programSettingsFormMixin from "@/mixins/programSettingsFormMixin";
import ProgramSettingsFormWrapper from "@@/ProgramSettingsAdmin/ProgramSettingsFormWrapper";
import WtSwitchQuestionInput from "@@/WtSwitchQuestionInput/WtSwitchQuestionInput";
import WtRadioQuestionInput from "@@/WtRadioQuestionInput/WtRadioQuestionInput";
import WtButton from "@@/WtButton/WtButton";
import WtLabel from "@@/WtLabel/WtLabel";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import WtRouterLink from "@@/WtRouterLink/WtRouterLink";

export default {
  name: "Settings",

  components: {
    WtButton,
    WtLabel,
    WtSwitchQuestionInput,
    WtRadioQuestionInput,
    WtTile,
    WtTitle,
    WtRouterLink,
    ProgramSettingsFormWrapper
  },

  mixins: [programSettingsFormMixin, numberUtilityMixin],

  data() {
    return {
      teamInviteNotificationsTooltip: `Allow Be Well to send an email to members when they are invited to join a team.`
    };
  },

  computed: {
    ...mapGetters("program", [
      "coreProgramMeta",
      "programSettingsCopy",
      "programLoaded"
    ]),

    // User cannot click "create team", if the maxTeamSize value has been changed and not saved
    maxTeamSizeSaved() {
      return this.isOriginalValue("maxTeamSize", this.maxTeamSize);
    },

    formData() {
      return {
        teamsOff: this.teamsOff,
        memberCanManageTeams: this.memberCanManageTeams,
        maxTeamSize: this.maxTeamSize ? this.sanitizeNum(this.maxTeamSize) : 1,
        teamInviteEmails: this.teamInviteEmails,
        teamJoinerOn: this.teamJoinerOn,
        teamJoiningOptional: this.teamJoiningOptional
      };
    },

    keysToReset() {
      return ["teamsOff", "memberCanManageTeams", "maxTeamSize"];
    },

    teamsOff: {
      set(teamsOff) {
        this.updateProgramSettingsCopy({
          key: "teamsOff",
          value: teamsOff
        });
      },
      get() {
        return Number(this.programSettingsCopy.teamsOff);
      }
    },

    memberCanManageTeams: {
      set(memberCanManageTeams) {
        this.updateProgramSettingsCopy({
          key: "memberCanManageTeams",
          value: memberCanManageTeams
        });
      },
      get() {
        return Number(this.programSettingsCopy.memberCanManageTeams);
      }
    },

    maxTeamSize: {
      set(maxTeamSize) {
        this.updateProgramSettingsCopy({
          key: "maxTeamSize",
          value: maxTeamSize
        });
      },
      get() {
        return Number(this.programSettingsCopy.maxTeamSize);
      }
    },

    teamJoinerOn: {
      set(teamJoinerOn) {
        this.updateProgramSettingsCopy({
          key: "teamJoinerOn",
          value: teamJoinerOn
        });
      },
      get() {
        return Number(this.programSettingsCopy.teamJoinerOn);
      }
    },

    teamInviteEmails: {
      set(teamInviteEmails) {
        this.updateProgramSettingsCopy({
          key: "teamInviteEmails",
          value: teamInviteEmails
        });
      },
      get() {
        return Number(this.programSettingsCopy.teamInviteEmails);
      }
    },

    teamJoiningOptional: {
      set(teamJoiningOptional) {
        this.updateProgramSettingsCopy({
          key: "teamJoiningOptional",
          value: teamJoiningOptional
        });
      },
      get() {
        return Number(this.programSettingsCopy.teamJoiningOptional);
      }
    }
  },

  methods: {
    ...mapActions("program", ["updateProgramSettingsCopy"]),

    handleMaxTeamSizeInput() {
      this.disableSave = this.isOriginalValue("maxTeamSize", this.maxTeamSize);
    },

    confirmTeamRemoval() {
      this.$buefy.dialog.confirm({
        title: "Turn Off Teams",
        confirmText: "Confirm",
        type: "is-danger",
        hasIcon: true,
        message:
          "Are you sure you want to turn off teams? Members will no longer have access to their teams, and any existing Team Challenges will be affected.",
        onConfirm: async () => {
          this.changeTeamsOff(1);
        },
        onCancel: () => {
          this.teamsOff = 0;
        }
      });
    },

    changeTeamsOff(value) {
      EventBus.emitSaveProgramSettings({
        formKey: "TeamsSettings",
        formData: {
          teamsOff: value
        }
      });
    }
  }
};
</script>

<style lang="sass">
#max-team-size
  max-width: 70px
.full-width
  width: 100%
.disabled
  opacity: 0.4
  pointer-events: none
</style>
